import React from 'react';
import { Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NOTIFICATION_EMAIL } from '../constants';

const Footer = ({ openPrivacy }) => {
    const [email, setEmail] = React.useState('');
    const [status, setStatus] = React.useState(null); // 'success', 'error'
    const [isEmailMenuOpen, setIsEmailMenuOpen] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const emailRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (emailRef.current && !emailRef.current.contains(event.target)) {
                setIsEmailMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCopyEmail = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(NOTIFICATION_EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleJoin = async (e) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!email) {
            setStatus({ type: 'error', message: 'Email is required' });
            return;
        }

        if (!emailRegex.test(email)) {
            setStatus({ type: 'error', message: 'Invalid email format' });
            return;
        }

        setStatus({ type: 'loading', message: 'Sending subscription request...' });

        try {
            const emailData = new FormData();
            emailData.append("_subject", "New Lead");
            emailData.append("_template", "table");
            emailData.append("_captcha", "false");
            emailData.append("Subscriber Email", email);
            emailData.append("Message", "Hello Excalibur Team,\n\nA new user wishes to get in touch with you.\n\nEmail Address: " + email + "\n\nPlease connect with them at your earliest convenience.\n\nBest regards,\nExcalibur Web Assistant");

            const response = await fetch(`https://formsubmit.co/ajax/${NOTIFICATION_EMAIL}`, {
                method: "POST",
                body: emailData
            });

            if (!response.ok) {
                throw new Error("Failed to send subscription request.");
            }

            setStatus({ type: 'success', message: 'Thank you for subscribing! We will get in touch with you shortly.' });
            setEmail('');
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Submission failed. Please try again.' });
        } finally {
            setTimeout(() => {
                setStatus(null);
            }, 5000);
        }
    };

    return (
        <footer className="relative w-full bg-white overflow-hidden">
            {/* Background Image with Responsive Handling */}
            <div
                className="absolute inset-0 z-0 bg-white"
                style={{
                    backgroundImage: `url('/footerbackground.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'top center',
                    backgroundRepeat: 'no-repeat'
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-28 sm:pt-32 lg:pt-48 pb-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-0 mb-[30px] lg:mb-16 items-start">

                    {/* Column 1: Logo & Contact - Centered on Mobile/Tab */}
                    <div className="flex flex-col items-center lg:items-start gap-4 lg:col-span-3 lg:pr-12 text-center lg:text-left">
                        <img
                            src="/videos/excalibur-logo.png"
                            alt="Excalibur Projects Business Logo"
                            loading="lazy"
                            className="h-12 sm:h-20 w-auto object-contain brightness-0 invert opacity-100"
                        />
                        <div className="flex flex-col gap-4">
                            <address className="not-italic text-white/60 text-xs md:text-sm font-medium leading-relaxed max-w-[280px]">
                                E-18, Lotus Corporate Park, <br />
                                near Western Express Highway, <br />
                                Goregaon East, Mumbai - 400063
                            </address>
                        </div>
                    </div>

                    {/* Column 2: Tagline & Search - Centered on Mobile/Tab */}
                    <div className="flex flex-col items-center justify-center gap-2 lg:gap-4 lg:col-span-6 px-4 lg:px-12 lg:border-x border-white/10 text-center">
                        <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight">Stay Connected</h3>
                        <p className="text-white/80 text-xs md:text-sm italic max-w-[320px] leading-relaxed mb-2">
                            "Redefining the standards of real estate excellence in Mumbai's most prime locations."
                        </p>
                        <div className="w-full max-w-sm flex flex-col items-center">
                            <form onSubmit={handleJoin} className="w-full flex">
                                <input
                                    type="email"
                                    placeholder="Enter Email"
                                    className="bg-transparent border border-white/20 rounded-l-xl px-4 py-3 text-xs text-white outline-none w-full focus:border-white/40 disabled:opacity-50"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={status?.type === 'loading'}
                                />
                                <button
                                    type="submit"
                                    disabled={status?.type === 'loading'}
                                    className="bg-white text-[#1B4332] font-black uppercase text-[10px] px-6 rounded-r-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                                >
                                    {status?.type === 'loading' ? 'JOINING...' : 'JOIN'}
                                </button>
                            </form>
                            {status && (
                                <p className={`mt-3 text-[10px] font-bold uppercase tracking-widest ${status.type === 'success' ? 'text-green-400' : status.type === 'loading' ? 'text-blue-400' : 'text-red-400'}`}>
                                    {status.message}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Column 3: Email Chip (Links Hidden on Mobile/Tab) */}
                    <div className="flex flex-col items-center lg:items-start gap-4 lg:gap-14 lg:col-span-3 lg:pl-12 text-center lg:text-left">
                        {/* Hidden on Tablet/Mobile */}
                        <div className="hidden lg:grid grid-cols-2 gap-x-8 gap-y-4">
                            {[
                                { name: 'About Us', href: '/' },
                                { name: 'Services', href: '#services' },
                                { name: 'Contact Us', href: '#contact' },
                                { name: 'Projects', href: '/' }
                            ].map((link) => (
                                <motion.a
                                    key={link.name}
                                    href={link.href}
                                    whileHover={{ x: 6, color: '#FFFFFF' }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    className="text-white/70 text-sm font-bold tracking-wide transition-colors"
                                >
                                    {link.name}
                                </motion.a>
                            ))}
                        </div>

                        {/* Email Branded Chip - Always Visible, Centered on Mobile */}
                        <div className="relative" ref={emailRef}>
                            <button
                                onClick={() => setIsEmailMenuOpen(!isEmailMenuOpen)}
                                className="flex items-center gap-3 bg-white/5 border border-white/10 px-5 py-2.5 rounded-full hover:bg-white/10 hover:border-white/20 transition-all group cursor-pointer"
                            >
                                <Mail className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" />
                                <span className="text-white/80 text-xs font-semibold tracking-wide group-hover:text-white transition-colors">
                                    {NOTIFICATION_EMAIL}
                                </span>
                            </button>

                            <AnimatePresence>
                                {isEmailMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute bottom-full mb-3 right-0 lg:right-auto lg:left-0 bg-[#121212]/95 backdrop-blur-md border border-white/10 rounded-2xl p-2 shadow-2xl flex flex-col gap-1 z-50 min-w-[220px]"
                                    >
                                        <a
                                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${NOTIFICATION_EMAIL}&su=Enquiry%20to%20Excalibur%20Projects`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/10 transition-colors text-white/90 hover:text-white text-xs font-semibold text-left"
                                            onClick={() => setIsEmailMenuOpen(false)}
                                        >
                                            <span className="w-2.5 h-2.5 rounded-full bg-[#EA4335]" />
                                            <span>Open in Gmail (Web)</span>
                                        </a>
                                        <a
                                            href={`mailto:${NOTIFICATION_EMAIL}?subject=Enquiry%20to%20Excalibur%20Projects`}
                                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/10 transition-colors text-white/90 hover:text-white text-xs font-semibold text-left"
                                            onClick={() => setIsEmailMenuOpen(false)}
                                        >
                                            <Mail className="w-3.5 h-3.5 text-white/60" />
                                            <span>Default Email Client</span>
                                        </a>
                                        <button
                                            onClick={handleCopyEmail}
                                            className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl hover:bg-white/10 transition-colors text-white/90 hover:text-white text-xs font-semibold text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <svg className="w-3.5 h-3.5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                                </svg>
                                                <span>Copy Email Address</span>
                                            </div>
                                            {copied && <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider ml-2">Copied!</span>}
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-4 lg:pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-6">
                    <p className="text-xs font-medium text-white/70 uppercase tracking-[0.2em] text-center sm:text-left">
                        &copy; 2026 Excalibur Projects. All Rights Reserved.
                    </p>
                    <div className="flex items-center gap-12">
                        <button 
                            onClick={(e) => { e.preventDefault(); openPrivacy(); }} 
                            className="text-xs font-bold text-white/90 hover:text-white transition-colors"
                        >
                            Privacy Policy
                        </button>
                        <a href="/" className="text-xs font-bold text-white/90 hover:text-white transition-colors">Terms of Service</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default React.memo(Footer);
